import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableComponent from "../../Global Functions/tableComponentsCp";
import { listSiteVisitRental } from "../../Redux/Actions/siteVisitActions";
import { EllipsisOutlined } from "@ant-design/icons";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Badge, Button, Dropdown, Menu, Tooltip } from "antd";
import { ToFirstCapital } from "../../Utils/helpers";
import { Link } from "react-router-dom";

const SiteVisitRentalList = () => {
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

    const { siteVisitRentalList, loading } = useSelector((state) => state.siteVisit);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const fetchDetails = React.useCallback(
        async (page, limit, showLoader = true) => {
            const data = {
                page,
                limit,
                filter: debouncedSearch,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };
            try {
                await dispatch(listSiteVisitRental(data, showLoader));
                if (!showLoader) {
                    // toast.success("Site visit rental list refreshed");
                }
            } catch {
                if (!showLoader) {
                    // toast.error("Failed to refresh site visit rental list");
                }
            }
        },
        [dispatch, debouncedSearch, dateRange]
    );

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const dataSource = siteVisitRentalList?.data?.map((item, index) => ({
        key: item._id,
        index: index + 1,
        property: item.property,
        property_id: item.property?.property_id || "N/A",
        property_name: item.property?.property_name || "N/A",
        property_address: item.property?.street_address || "N/A",
        user_name: item.user?.name || "N/A",
        user_email: item.user?.email || "N/A",
        user_contact: item.user
            ? `${item.user.country_code || ""} ${item.user.contact_number || ""}`.trim()
            : "N/A",
        timeSlots: item.timeSlots,
        site_visit_date_time: (() => {
            const dateStr = item.site_visit_date_time || null;
            if (!dateStr) return "N/A";
            const localTimeStr = dateStr.endsWith("Z") ? dateStr.slice(0, -1) : dateStr;
            return new Date(localTimeStr).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        })(),
        created_at: item.created_at,
        cancelled_reason: item.cancelled_reason || "-",
        status: item.status,
    }));

    const columns = [
        {
            title: "Sr No.",
            align: "center",
            render: (_, __, index) => index + 1,
        },
        {
            title: "Property ID",
            dataIndex: "property_id",
            key: "property_id",
            align: "center",
        },
        {
            title: "Property",
            key: "property",
            align: "center",
            render: (_, record) => (
                <div>
                    <Tooltip title={record.property_address !== "N/A" ? record.property_address : ""}>
                        <div
                            style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 200,
                                margin: "0 auto",
                            }}
                        >
                            {record.property_address}
                        </div>
                    </Tooltip>
                    {record.property_id !== "N/A" && (
                        <div style={{ fontSize: 12, color: "#888" }}>
                            {record.property_id}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Property Address",
            dataIndex: "property_address",
            key: "property_address",
            align: "center",
            ellipsis: true,
        },
        {
            title: "User Name",
            dataIndex: "user_name",
            key: "user_name",
            align: "center",
        },
        {
            title: "Contact Number",
            dataIndex: "user_contact",
            key: "user_contact",
            align: "center",
        },
        {
            title: "Time Slot",
            dataIndex: "timeSlots",
            key: "timeSlots",
            align: "center",
        },
        {
            title: "Visit Date",
            dataIndex: "site_visit_date_time",
            key: "site_visit_date_time",
            align: "center",
        },
        {
            title: "Upload Date",
            align: "center",
            dataIndex: "created_at",
            key: "created",
            render: (record) => (record ? new Date(record).toLocaleString() : ""),
        },
        {
            title: "Status",
            dataIndex: "status",
            align: "center",
            key: "status",
            render: (status) => {
                const getStatusColor = (status) => ({
                    requested: "orange",
                    confirmed: "blue",
                    ongoing: "yellow",
                    completed: "green",
                    cancelled: "red",
                }[status] || "default");

                return (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 8px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "10px",
                        }}
                    >
                        <Badge color={getStatusColor(status)} text={ToFirstCapital(status)} />
                    </span>
                );
            },
        },
        {
            title: "Action",
            align: "center",
            render: (_, record) => (
                <Dropdown
                    trigger={["click"]}
                    overlay={
                        <Menu>
                            <Link to={`/site-visit-rental/${record.key}`}>
                                <Menu.Item key="view">View</Menu.Item>
                            </Link>
                        </Menu>
                    }
                >
                    <Button shape="square" icon={<EllipsisOutlined />} />
                </Dropdown>
            ),
        },
    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchDetails(pagination.current, pagination.pageSize);
        }, 150);

        return () => clearTimeout(timer);
    }, [pagination.current, pagination.pageSize, fetchDetails]);

    useEffect(() => {
        if (siteVisitRentalList?.count !== undefined) {
            setPagination((prevData) => ({
                ...prevData,
                total: siteVisitRentalList.count,
            }));
        }
    }, [siteVisitRentalList]);

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    return (
        <>
            <TableComponent
                h3heading="Site Visit Rental"
                pagination={pagination}
                dataSource={dataSource}
                onChange={handleTableChange}
                columns={columns}
                setState={setSearchQuery}
                onCalendarClick={() => setIsDatePopupVisible(true)}
                loading={loading}
            ></TableComponent>

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default SiteVisitRentalList;